import React from 'react'
import { Braces, ChevronRight } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

/**
 * EngineDetails — the diagnostics disclosure.
 *
 * The ONE place raw engine internals are allowed to live: the bare `0.53x` ratios, `σ`,
 * sample counts, amplifiers, `Trimmed +7.5% → +3.0%`, forecast bands, computed→applied.
 * The system is under test, so these values must stay reachable one click away for catching
 * data bugs — but collapsed, labelled technical, and OFF the glance surfaces. Everything
 * inside is VERBATIM / untranslated: prettifying diagnostics would defeat their purpose.
 *
 * Works at three scales:
 *  - `variant="icon"`    — an icon-only affordance at the end of a modal row / card corner.
 *    No visible text on the surface at all: a small muted braces glyph opens a compact
 *    popover holding the verbatim rows. This is the ROW-scale default for glance-adjacent
 *    surfaces (the old visible "HOW THIS WAS CALCULATED · TECHNICAL" row distracted
 *    normal users). Keyboard accessible (real button, `aria-expanded` from Radix,
 *    focus ring); labelled via `aria-label`/`title` "How this was calculated".
 *  - `variant="row"`     — the labelled `<details>` summary row (legacy row scale).
 *  - `variant="section"` — a page-level "Engine diagnostics" block (TargetDetail).
 *
 * Row/section are built on native `<details>`/`<summary>` so they are keyboard-accessible
 * for free (Enter/Space toggles, focus ring visible) and work without JS. Respects
 * `prefers-reduced-motion`. Styled subordinate — muted, smaller — so it reads as
 * secondary, never enticing.
 */

export interface EngineDetailsProps {
  /** Un-enticing technical label. Defaults per variant (icon: aria-label/title only). */
  label?: string
  variant?: 'row' | 'section' | 'icon'
  /** Rarely used — diagnostics stay collapsed by default so they're off the glance surface. */
  defaultOpen?: boolean
  className?: string
  children: React.ReactNode
}

/**
 * Icon-only trigger + popover. The trigger sits inline (end of a row, card corner) with no
 * visible text; a parent may add `group/row` to make the glyph strengthen on row hover.
 * The popover body renders the same verbatim `EngineDetails.Row` children as the other
 * variants — collapsed (closed) by default, one click away.
 */
function EngineDetailsIcon({ label, defaultOpen = false, className, children }: EngineDetailsProps) {
  const resolvedLabel = label ?? 'How this was calculated'
  return (
    <Popover defaultOpen={defaultOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={resolvedLabel}
          title={resolvedLabel}
          className={cn(
            'engine-details-icon inline-flex h-4 w-4 shrink-0 items-center justify-center rounded',
            'text-muted-foreground/40 transition-colors hover:text-muted-foreground',
            // Hover-reveal: strengthens when the containing row/card is hovered (opt-in
            // via a `group/row` class on the parent) and while the popover is open.
            'group-hover/row:text-muted-foreground/70 data-[state=open]:text-muted-foreground',
            'outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-1',
            className,
          )}
        >
          <Braces className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto min-w-[220px] max-w-[300px] px-3 py-2">
        <div className="mb-1 text-[9px] font-medium uppercase tracking-wide text-muted-foreground/60">
          {resolvedLabel}
          <span className="ml-1 font-normal text-muted-foreground/50 normal-case tracking-normal">technical</span>
        </div>
        <div className="space-y-0.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
          {children}
        </div>
      </PopoverContent>
    </Popover>
  )
}

function EngineDetailsBase({ label, variant = 'row', defaultOpen = false, className, children }: EngineDetailsProps) {
  if (variant === 'icon') {
    return (
      <EngineDetailsIcon label={label} defaultOpen={defaultOpen} className={className}>
        {children}
      </EngineDetailsIcon>
    )
  }

  const isSection = variant === 'section'
  const resolvedLabel = label ?? (isSection ? 'Engine diagnostics' : 'How this was calculated')

  return (
    <details
      open={defaultOpen}
      className={cn('engine-details group/engine', isSection ? 'rounded-lg border border-border/60 bg-muted/30' : '', className)}
    >
      <summary
        className={cn(
          'flex cursor-pointer list-none items-center gap-1 select-none text-muted-foreground',
          'outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-1 rounded',
          'hover:text-foreground transition-colors',
          isSection ? 'px-3 py-2 text-xs font-medium' : 'text-[10px] font-medium uppercase tracking-wide',
        )}
      >
        <ChevronRight
          className={cn(
            'h-3 w-3 shrink-0 text-muted-foreground/60 transition-transform duration-200 group-open/engine:rotate-90 motion-reduce:transition-none',
          )}
          aria-hidden="true"
        />
        <span>{resolvedLabel}</span>
        <span className="ml-1 text-[9px] font-normal text-muted-foreground/50">technical</span>
      </summary>
      <div className={cn('space-y-0.5 font-mono text-[10px] leading-relaxed text-muted-foreground', isSection ? 'px-3 pb-3 pt-1' : 'pt-1.5')}>
        {children}
      </div>
    </details>
  )
}

/**
 * A single verbatim key/value diagnostic row. `value` is rendered exactly as passed —
 * do NOT translate or reformat it here (that is the whole point of the disclosure).
 */
function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-muted-foreground/70">{label}</span>
      <span className="tabular-nums text-foreground/80">{value}</span>
    </div>
  )
}

type EngineDetailsComponent = typeof EngineDetailsBase & { Row: typeof Row }

const EngineDetails = EngineDetailsBase as EngineDetailsComponent
EngineDetails.Row = Row

export default EngineDetails
